import { Dispatch, SetStateAction } from 'react';
import { Content, ContentType, ExtrasContent } from '../model/extras.domain';
import { ExtrasModel } from '../model/extras.model';
import {
  getContentName,
  toMixpanelContentType,
  togglePublicationStatus,
  toUpdateContentInput,
  updateTreeItems,
} from './extras.mappers';
import { Draggable, ModalMode } from './extras.types';
import { v4 } from 'uuid';
import { trackEvent } from '@services/tracking';
import toast from 'react-hot-toast';
import { Filters } from './extras.viewModel';

type Setter<T> = Dispatch<SetStateAction<T>>;

type SaveTabDeps = {
  modalModeType: ModalMode;
  currentId: string;
  model: ExtrasModel;
  setModalModeType: Setter<ModalMode>;
  setFormType: Setter<ContentType>;
};

type SetSearchDeps = { setSearch: Setter<string> };

type SetFilterTypeDeps = { filters: Filters; setFilters: Setter<Filters> };

type CommentsDeps = { setCommentsContentId: Setter<string | null> };

type CloseModalDeps = {
  setModalModeType: Setter<ModalMode>;
  setFormType: Setter<ContentType>;
  setCurrentId: Setter<string>;
};

type ToggleVisibleDeps = {
  state: ExtrasContent;
  setState: Setter<ExtrasContent>;
  setCurrentId: Setter<string>;
  model: ExtrasModel;
  setShowFirstPublishConfetti: Setter<boolean>;
};

type ReorderDeps = {
  setState: Setter<ExtrasContent>;
  model: ExtrasModel;
};

type DeleteDeps = {
  setCurrentId: Setter<string>;
  setModalModeType: Setter<ModalMode>;
};

type SubmitDeps = {
  handleSubmit: (content: Content) => Promise<unknown>;
  currentId: string;
};

type ConfirmDeleteDeps = {
  currentId: string;
  state: ExtrasContent;
  model: ExtrasModel;
  setModalModeType: Setter<ModalMode>;
  setCurrentId: Setter<string>;
};

export const handleSaveTab = (
  name: string,
  {
    modalModeType,
    currentId,
    model,
    setModalModeType,
    setFormType,
  }: SaveTabDeps,
): Promise<unknown> | void => {
  trackEvent({
    type: 'Extra Tab Named and Saved',
    payload: {
      'Tab Name': name,
    },
  });

  if (model.data.type !== 'fetched') return;

  if (modalModeType === 'create') {
    return model
      .create({
        input: {
          type: 'ContentTab',
          value: {
            parentId: '',
            isPublished: false,
            id: v4(),
            name,
          },
        },
      })
      .then(() => {
        setModalModeType('none');
        setFormType('ContentLink');
      });
  }

  if (modalModeType === 'edit' && currentId) {
    const contentTab = model.data.value.contentMap[currentId];

    if (!contentTab || contentTab.type !== 'ContentTab') return;

    return model
      .update(
        currentId,
        toUpdateContentInput({
          ...contentTab,
          value: {
            ...contentTab.value,
            name,
          },
        }),
      )
      .then(() => {
        setModalModeType('none');
        setFormType('ContentLink');
      });
  }
};

export const handleSetSearch = (
  value: string,
  { setSearch }: SetSearchDeps,
) => {
  setSearch(value);
};

export const handleSetFilterType = (
  type: Partial<Filters>,
  { filters, setFilters }: SetFilterTypeDeps,
) => {
  setFilters({ ...filters, ...type });
};

export const handleOpenComments = (
  contentId: string,
  { setCommentsContentId }: CommentsDeps,
) => {
  setCommentsContentId(contentId);
};

export const handleCloseComments = ({ setCommentsContentId }: CommentsDeps) => {
  setCommentsContentId(null);
};

export const handleCloseModal = ({
  setModalModeType,
  setFormType,
  setCurrentId,
}: CloseModalDeps) => {
  setModalModeType('none');
  setFormType('ContentLink');
  setCurrentId('');
};

export const handleToggleItemVisible = (
  contentId: string,
  {
    state,
    setState,
    setCurrentId,
    model,
    setShowFirstPublishConfetti,
  }: ToggleVisibleDeps,
) => {
  if (model.data.type !== 'fetched') return;
  const content = state.contentMap[contentId];
  const updatedContent = togglePublicationStatus(content);

  setCurrentId(contentId);

  const isPublishing =
    updatedContent.value.purchaseInfo.type === 'purchasable' &&
    updatedContent.value.purchaseInfo.publicationStatus === 'published';

  const hasAnyPublishedExtra = Object.values(state.contentMap).some(
    (c) =>
      c.type !== 'ContentTab' &&
      c.value.purchaseInfo.type === 'purchasable' &&
      c.value.purchaseInfo.publicationStatus === 'published',
  );

  const isFirstPublish = isPublishing && !hasAnyPublishedExtra;

  model
    .update(updatedContent.value.id, toUpdateContentInput(updatedContent))
    .then(() => {
      trackEvent({
        type: 'Extras Block visibility toggled',
        payload: {
          type: toMixpanelContentType(content.type),
          title: getContentName(content),
          visibility: isPublishing,
        },
      });

      if (isFirstPublish) {
        setShowFirstPublishConfetti(true);
        toast.success('Content is published and visible to users');
      }
    })
    .catch(() => {
      toast.error('Failed to change visibility');
      setState((prev) => ({
        ...prev,
        contentMap: {
          ...prev.contentMap,
          [contentId]: content,
        },
      }));
    });

  setState((prev) => ({
    ...prev,
    contentMap: {
      ...prev.contentMap,
      [contentId]: updatedContent,
    },
  }));
};

export const handleReorder = (
  items: Draggable[],
  { setState, model }: ReorderDeps,
) => {
  setState((prev) => {
    const updatedContentIds = updateTreeItems(
      prev.contentIds,
      (prev.contentIds.items || [])[0].id,
      items.map((item) => ({ id: item.id })),
    );

    model.reorder(updatedContentIds).catch(() => {
      toast.error('Reorder failed');
      setState((innerPrev) => ({
        ...innerPrev,
        contentIds: innerPrev.contentIds,
      }));
    });

    return {
      ...prev,
      contentIds: updatedContentIds,
    };
  });
};

export const handleDelete = (
  contentId: string,
  { setCurrentId, setModalModeType }: DeleteDeps,
) => {
  setCurrentId(contentId);
  setModalModeType('delete');
};

export const handleSubmitAction = (
  content: Content,
  { handleSubmit, currentId }: SubmitDeps,
) => {
  if (!currentId) return;
  handleSubmit(content).catch(() => {
    toast.error('Failed to save content');
  });
};

export const handleConfirmDelete = ({
  currentId,
  state,
  model,
  setModalModeType,
  setCurrentId,
}: ConfirmDeleteDeps) => {
  if (!currentId) return;
  const content = state.contentMap[currentId];
  if (!content) return;

  model
    .delete(content)
    .then(() => {
      trackEvent({
        type: 'Extras Block Deleted',
        payload: {
          type: toMixpanelContentType(content.type),
          title: getContentName(content),
        },
      });
      setModalModeType('none');
      setCurrentId('');
    })
    .catch(() => {
      console.error('Delete failed');
    });
};

export type {
  SaveTabDeps,
  SetSearchDeps,
  SetFilterTypeDeps,
  CommentsDeps,
  CloseModalDeps,
  ToggleVisibleDeps,
  ReorderDeps,
  DeleteDeps,
  SubmitDeps,
  ConfirmDeleteDeps,
};
